import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TuiIcon, TuiHint } from '@taiga-ui/core';

@Component({
  selector: 'x-file-preview',
  imports: [CommonModule, TuiHint, TuiIcon],
  templateUrl: './file-preview.component.html',
  styleUrl: './file-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePreviewComponent {
  private readonly srcSignal = signal('');
  private readonly titleSignal = signal('');
  private readonly fallbackSignal = signal<string[] | undefined>(undefined);
  private readonly badgeSignal = signal<string | undefined>(undefined);
  private readonly fallbackIconSignal = signal<string | null>(null);
  private readonly widthSignal = signal<string | number>('128px');
  private readonly heightSignal = signal<string | number>('128px');

  protected readonly $src = computed(() => this.srcSignal());
  protected readonly $title = computed(() => this.titleSignal());
  protected readonly $fallback = computed(() => this.fallbackSignal());
  protected readonly $badge = computed(() => this.badgeSignal());
  protected readonly $fallbackIcon = computed(() => this.fallbackIconSignal());
  protected readonly $currentSrc = signal<string>('');
  protected readonly $showFallbackIcon = signal(false);
  protected readonly $previewWidth = computed(() =>
    this.toCssSize(this.widthSignal()),
  );
  protected readonly $previewHeight = computed(() =>
    this.toCssSize(this.heightSignal()),
  );
  private fallbackIndex = 0;

  @Input({ required: true })
  set src(value: string) {
    this.srcSignal.set(value);
    this.$currentSrc.set(value);
    this.$showFallbackIcon.set(false);
    this.fallbackIndex = 0;
  }

  @Input({ required: true })
  set title(value: string) {
    this.titleSignal.set(value);
  }

  @Input()
  set fallback(value: string[] | undefined) {
    this.fallbackSignal.set(value);
  }

  @Input()
  set badge(value: string | undefined) {
    this.badgeSignal.set(value);
  }

  @Input()
  set fallbackIcon(value: string | null) {
    this.fallbackIconSignal.set(value);
  }

  @Input()
  set width(value: string | number) {
    this.widthSignal.set(value);
  }

  @Input()
  set height(value: string | number) {
    this.heightSignal.set(value);
  }

  protected onImgError(): void {
    const fallbacks = this.$fallback();

    if (fallbacks && this.fallbackIndex < fallbacks.length) {
      this.$currentSrc.set(fallbacks[this.fallbackIndex++]);
      this.$showFallbackIcon.set(false);
    } else {
      this.$showFallbackIcon.set(!!this.$fallbackIcon());
    }
  }

  private toCssSize(value: string | number): string {
    return typeof value === 'number' ? `${value}px` : value;
  }
}
